import * as integrationSagas from './integrations';
import { sagas as benchmarkSagas } from './benchmarks';
import * as billingSagas from './billing';
import * as contributorsSagas from './Contributors/index';
import * as installationsSagas from './installations';
import * as loginSagas from './login';
import * as notesSagas from './notes';
import * as onpremSagas from './onprem';
import * as organizationDataSagas from './organizationData';
import * as organizationMembersSagas from './organizationMembers';
import * as organizationSagas from './organizations';
import * as projectsDataSagas from './projectData';
import * as projectsSagas from './projects';
import * as pullRequestsSagas from './pullRequests';
import * as ratingsSagas from './ratings';
import * as reviewsSagas from './reviews';
import * as reviewRulesSagas from './reviewRules';
import * as signupsSagas from './signups';
import { navigate } from './Navigation';
import { fetchDashboardData } from './Dashboard';
import { fetchProjectDashboardData } from './ProjectDashboard';
import {
  fetchCommentHistoryData,
  fetchCommentHistorySummary,
} from './CommentHistory';
import { fetchSnippets } from './CommentHistory/Snippets';

import { all, fork, takeEvery, takeLatest } from 'redux-saga/effects';

import { types as installationsTypes } from '../reducers/installations';
import { types as integrationTypes } from '../reducers/accountIntegrations';
import { types as billingTypes } from '../reducers/billing';
import { types as contributorsTypes } from '../reducers/Contributors/index';
import { types as loginTypes } from '../reducers/login';
import { types as memberTypes } from '../reducers/organizationMembers';
import { types as notesTypes } from '../reducers/notes';
import { types as onpremTypes } from '../reducers/onprem';
import { types as organizationDataTypes } from '../reducers/organizationData';
import { types as organizationsTypes } from '../reducers/organizations';
import { types as projectDataTypes } from '../reducers/projectData';
import { types as projectsTypes } from '../reducers/projects';
import { types as pullRequestsTypes } from '../reducers/pullRequests';
import { types as ratingsTypes } from '../reducers/ratings';
import { types as reviewsTypes } from '../reducers/reviews';
import { types as reviewRulesTypes } from '../reducers/reviewRules';
import { types as signupsTypes } from '../reducers/signups';
import { Type as navigationTypes } from '../reducers/Navigation';
import { Type as dashboardTypes } from 'reducers/Dashboard';
import { Type as projectDashboardTypes } from 'reducers/ProjectDashboard';
import { Type as commentHistoryControlTypes } from 'reducers/CommentHistory/Controls';
import { Type as commentHistoryContextTypes } from 'reducers/CommentHistory/CommentContexts';

import { accountSaga } from './Account';
import { themeSaga } from './Theme';
import { sagas as sagasV2 } from 'stores';

export default function* rootSaga() {
  yield fork(accountSaga);
  yield fork(themeSaga);
  yield fork(sagasV2);

  yield all([
    ...benchmarkSagas,
    takeEvery(
      installationsTypes.INSTALLATIONS_REQUEST,
      installationsSagas.fetchInstallations
    ),
    takeEvery(
      installationsTypes.UPDATE_INSTALLATIONS_REQUEST,
      installationsSagas.updateInstallation
    ),
    takeEvery(
      integrationTypes.INTEGRATIONS_REQUEST,
      integrationSagas.fetchIntegrations
    ),
    takeEvery(loginTypes.LOGIN_REQUEST, loginSagas.login),
    takeEvery(loginTypes.LOGOUT_REQUEST, loginSagas.logout),
    takeEvery(
      loginTypes.STOP_IMPERSONATING_REQUEST,
      loginSagas.stopImpersonating
    ),
    takeEvery(loginTypes.VERIFY_REQUEST, loginSagas.verify),
    takeEvery(
      loginTypes.REQUEST_RESET_EMAIL_REQUEST,
      loginSagas.requestResetEmail
    ),
    takeEvery(loginTypes.RESET_PASSWORD_REQUEST, loginSagas.resetPassword),
    takeEvery(
      memberTypes.INVITE_MISSING_MEMBERS_REQUEST,
      organizationSagas.inviteMissingMembers
    ),
    takeEvery(
      memberTypes.FETCH_MEMBERSHIP_DATA_REQUEST,
      organizationMembersSagas.fetchMembershipData
    ),
    takeEvery(
      memberTypes.MEMBERS_REQUEST,
      organizationMembersSagas.fetchOrganizationMembers
    ),
    takeEvery(
      memberTypes.MISSING_MEMBERS_REQUEST,
      organizationSagas.fetchMissingMembers
    ),
    takeEvery(
      organizationDataTypes.AGGREGATE_METADATA_REQUEST,
      organizationDataSagas.fetchAggregateMetadata
    ),
    takeEvery(
      organizationDataTypes.ORGANIZATION_PULL_REQUEST_AUTHORS_REQUEST,
      organizationDataSagas.fetchOrganizationPullRequestAuthors
    ),
    takeEvery(notesTypes.NOTES_REQUEST, notesSagas.fetchNotes),
    takeEvery(
      memberTypes.OWNERSHIP_REQUEST,
      organizationMembersSagas.toggleOwnership
    ),
    takeEvery(
      organizationsTypes.ORGANIZATION_REQUEST,
      organizationSagas.fetchDetailedOrganization
    ),
    takeEvery(
      billingTypes.GET_BILLING_INFO_REQUEST,
      billingSagas.fetchBillingInfo
    ),
    takeEvery(
      contributorsTypes.GET_CONTRIBUTORS_REQUEST,
      contributorsSagas.fetchContributors
    ),
    takeEvery(
      contributorsTypes.UPDATE_CONTRIBUTORS_REQUEST,
      contributorsSagas.updateContributors
    ),
    takeEvery(
      billingTypes.UPDATE_INVOICE_CONTACT_REQUEST,
      billingSagas.updateInvoiceContactInfo
    ),
    takeEvery(
      organizationsTypes.INVOICE_REQUEST,
      organizationSagas.fetchOrganizationInvoiceHistory
    ),
    takeEvery(
      organizationsTypes.TOGGLE_NOTES_LOCK_REQUEST,
      organizationSagas.toggleNotesLock
    ),
    takeEvery(
      organizationsTypes.TOGGLE_FULL_REPO_SEARCH_REQUEST,
      organizationSagas.toggleFullRepoSearch
    ),
    takeEvery(
      organizationsTypes.UPDATE_BILLING_PLAN_REQUEST,
      organizationSagas.updateOrganizationBillingPlan
    ),
    takeEvery(
      organizationsTypes.SUBSCRIPTION_CODE_REVIEW_REQUEST,
      organizationSagas.initializeCodeReviewSubscription
    ),
    takeEvery(
      organizationsTypes.SUBSCRIPTION_STATIC_REQUEST,
      organizationSagas.initializeStaticSubscription
    ),
    takeEvery(
      organizationsTypes.SUBSCRIPTION_CODE_REVIEW_RENEW_REQUEST,
      organizationSagas.renewCodeReviewSubscription
    ),
    takeEvery(
      organizationsTypes.SUBSCRIPTION_STATIC_RENEW_REQUEST,
      organizationSagas.renewStaticSubscription
    ),
    takeEvery(
      organizationsTypes.SUBSCRIPTION_CODE_REVIEW_CANCEL_REQUEST,
      organizationSagas.cancelCodeReviewSubscription
    ),
    takeEvery(
      organizationsTypes.SUBSCRIPTION_STATIC_CANCEL_REQUEST,
      organizationSagas.cancelStaticSubscription
    ),
    takeEvery(
      organizationsTypes.ORGANIZATION_PRODUCT_REQUEST,
      organizationSagas.updateOrganizationProduct
    ),
    takeEvery(
      onpremTypes.CREATE_DEPLOYMENT_REQUEST,
      onpremSagas.createDeployment
    ),
    takeEvery(
      onpremTypes.DELETE_DEPLOYMENT_REQUEST,
      onpremSagas.deleteDeployment
    ),
    takeEvery(onpremTypes.MANUAL_UPGRADE_REQUEST, onpremSagas.manualUpgrade),
    takeEvery(onpremTypes.SET_AUTO_UPGRADE_REQUEST, onpremSagas.setAutoUpgrade),
    takeEvery(onpremTypes.TEST_CONNECTION_REQUEST, onpremSagas.testConnection),
    takeEvery(
      projectsTypes.MARK_PROJECT_ONBOARDED_REQUEST,
      projectsSagas.markProjectOnboarded
    ),
    takeEvery(
      organizationsTypes.MARK_PROJECTS_ONBOARDED_REQUEST,
      organizationSagas.markProjectsOnboarded
    ),
    takeEvery(
      organizationsTypes.UPDATE_BILLING_INFO_REQUEST,
      organizationSagas.updateOrganizationBillingInfo
    ),
    takeEvery(projectsTypes.PROJECTS_REQUEST, projectsSagas.fetchProjects),
    takeLatest(
      projectsTypes.PROJECTS_SUCCESS,
      projectsSagas.navigateAfterProjectsFetch
    ),
    takeEvery(projectsTypes.PROJECT_REQUEST, projectsSagas.fetchProject),
    takeEvery(
      projectsTypes.TOGGLE_APPROVALS_REQUEST,
      projectsSagas.toggleApprovalsAllowed
    ),
    takeEvery(
      projectsTypes.TOGGLE_AUTO_REVIEW_REQUEST,
      projectsSagas.toggleAutoReview
    ),
    takeEvery(
      projectsTypes.BULK_TOGGLE_REVIEW_REQUEST,
      projectsSagas.bulkToggleAutoReview
    ),
    takeEvery(
      projectsTypes.TOGGLE_AUTOMATION_REQUEST,
      projectsSagas.toggleAutomationResults
    ),
    takeEvery(
      projectsTypes.UPDATE_PROJECT_NOTES_REQUEST,
      projectsSagas.updateProjectNotes
    ),
    takeEvery(
      projectsTypes.UPDATE_BILLING_PLAN_REQUEST,
      projectsSagas.updateProjectBillingPlan
    ),
    takeEvery(
      projectDataTypes.PROJECT_STATIC_FILTERS_REQUEST,
      projectsDataSagas.fetchProjectStaticFilters
    ),
    takeEvery(
      projectDataTypes.PROJECT_CREATE_STATIC_FILTER_REQUEST,
      projectsDataSagas.createProjectStaticFilter
    ),
    takeEvery(
      projectDataTypes.PROJECT_DELETE_STATIC_FILTER_REQUEST,
      projectsDataSagas.deleteProjectStaticFilter
    ),
    takeEvery(
      projectDataTypes.PROJECT_PULL_REQUEST_AUTHORS_REQUEST,
      projectsDataSagas.fetchProjectPullRequestAuthors
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_REQUEST,
      pullRequestsSagas.fetchPullRequest
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_COMMITS_REQUEST,
      pullRequestsSagas.fetchPullRequestCommits
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_EVENTS_REQUEST,
      pullRequestsSagas.fetchPullRequestEvents
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_COMMENTS_REQUEST,
      pullRequestsSagas.fetchPullRequestComments
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_COMMENT_SNIPPETS_REQUEST,
      pullRequestsSagas.fetchPullRequestCommentSnippets
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_REVIEWS_REQUEST,
      pullRequestsSagas.fetchPullRequestReviews
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_STATIC_RESULTS_REQUEST,
      pullRequestsSagas.fetchPullRequestStaticResults
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_STATIC_RESULTS_SNIPPETS_REQUEST,
      pullRequestsSagas.fetchPullRequestStaticResultSnippets
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_STATIC_TASK_REQUEST,
      pullRequestsSagas.fetchPullRequestStaticTask
    ),
    takeEvery(
      pullRequestsTypes.RESTART_STATIC_JOB_REQUEST,
      pullRequestsSagas.restartStaticJob
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_STATIC_TASK_ERROR_LOG_REQUEST,
      pullRequestsSagas.fetchStaticTaskErrorLog
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_DIFF_REQUEST,
      pullRequestsSagas.fetchPullRequestDiff
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_COLLAPSE_ELEMENT_REQUEST,
      pullRequestsSagas.collapseInlineElement
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_EXPAND_ELEMENT_REQUEST,
      pullRequestsSagas.expandInlineElement
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_SNOOZE_RESULT_REQUEST,
      pullRequestsSagas.snoozeStaticResult
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_UNDO_SNOOZE_RESULT_REQUEST,
      pullRequestsSagas.undoSnoozeStaticResult
    ),
    takeEvery(
      pullRequestsTypes.PULL_REQUEST_SNOOZED_RESULTS_REQUEST,
      pullRequestsSagas.fetchSnoozedStaticResults
    ),
    takeEvery(ratingsTypes.RATING_CONFIRM_REQUEST, ratingsSagas.confirmRating),
    takeEvery(ratingsTypes.RATING_STARS_REQUEST, ratingsSagas.postRating),
    takeEvery(ratingsTypes.RATING_FEEDBACK_REQUEST, ratingsSagas.postFeedback),
    takeEvery(
      ratingsTypes.RATING_TIE_RATINGS_REQUEST,
      ratingsSagas.tieUserToUnauthedRatings
    ),
    takeEvery(
      pullRequestsTypes.REREQUEST_REVIEW_REQUEST,
      pullRequestsSagas.rerequestReview
    ),
    takeEvery(
      reviewsTypes.RENEW_SUBSCRIPTION_TOGGLE_REVIEW_REQUEST,
      reviewsSagas.renewSubscriptionAndToggleReview
    ),
    takeEvery(
      reviewRulesTypes.REVIEW_RULES_REQUEST,
      reviewRulesSagas.fetchReviewRules
    ),
    takeEvery(
      reviewRulesTypes.CREATE_REVIEW_RULES_REQUEST,
      reviewRulesSagas.createReviewRule
    ),
    takeEvery(
      reviewRulesTypes.UPDATE_REVIEW_RULES_REQUEST,
      reviewRulesSagas.updateReviewRule
    ),
    takeEvery(
      reviewRulesTypes.DELETE_REVIEW_RULES_REQUEST,
      reviewRulesSagas.deleteReviewRule
    ),
    takeEvery(
      reviewRulesTypes.REVIEW_RULES_SELECTIONS_REQUEST,
      reviewRulesSagas.fetchReviewRulesSelections
    ),
    takeEvery(
      reviewRulesTypes.REORDER_REVIEW_RULES_REQUEST,
      reviewRulesSagas.reorderReviewRules
    ),
    takeEvery(
      signupsTypes.REVIEWER_TERMS_REQUEST,
      signupsSagas.acceptReviewerTerms
    ),
    takeEvery(signupsTypes.REF_REQUEST, signupsSagas.fetchRef),
    takeEvery(
      signupsTypes.DEVELOPER_SIGNUP_REQUEST,
      signupsSagas.developerSignUp
    ),
    takeEvery(
      signupsTypes.REVIEWER_SIGNUP_REQUEST,
      signupsSagas.reviewerSignUp
    ),
    takeEvery(
      signupsTypes.UPDATE_REVIEWER_PROFILE_REQUEST,
      signupsSagas.updateReviewerProfile
    ),
    takeEvery(
      signupsTypes.ADD_REVIEWER_INFO_REQUEST,
      signupsSagas.addReviewerInfo
    ),
    takeEvery(
      signupsTypes.STORE_SKILLS_REQUEST,
      signupsSagas.storeReviewerSkills
    ),
    takeEvery(
      signupsTypes.COMPLETE_REVIEWER_SIGNUP_REQUEST,
      signupsSagas.completeReviewerSignUp
    ),
    takeEvery(signupsTypes.PROMO_REQUEST, signupsSagas.fetchPromo),
    takeEvery(signupsTypes.SKILLS_REQUEST, signupsSagas.fetchSkills),
    takeEvery(
      signupsTypes.SLACK_INVITE_REQUEST,
      signupsSagas.getReviewerSlackUrl
    ),
    takeEvery(navigationTypes.NAVIGATE, navigate),
    takeLatest(dashboardTypes.FETCH_DASHBOARD_DATA, fetchDashboardData),
    takeLatest(
      projectDashboardTypes.FETCH_PROJECT_DASHBOARD_DATA,
      fetchProjectDashboardData
    ),
    takeLatest(
      [
        commentHistoryControlTypes.SET_ORGANIZATION_UUID,
        commentHistoryControlTypes.SET_DATE_RANGE,
      ],
      fetchCommentHistorySummary
    ),
    takeLatest(
      [
        commentHistoryControlTypes.SET_ORGANIZATION_UUID,
        commentHistoryControlTypes.SET_DATE_RANGE,
        commentHistoryControlTypes.SET_COMMENT_TYPE,
        commentHistoryControlTypes.SET_SEVERITY,
        commentHistoryControlTypes.SET_CATEGORY,
        commentHistoryControlTypes.SET_AUTHOR,
        commentHistoryControlTypes.SET_REPO_UUID,
        commentHistoryControlTypes.RESET_FILTERS,
        commentHistoryControlTypes.SET_SORT_BY,
        commentHistoryControlTypes.SET_PAGE,
      ],
      fetchCommentHistoryData
    ),
    takeLatest(commentHistoryContextTypes.SET_RESULT, fetchSnippets),
  ]);
}
